import { makeStyles, tokens, Card, Title3 } from '@fluentui/react-components';
import { PlugConnectedRegular } from '@fluentui/react-icons';
import { useMsal } from '@azure/msal-react';
import { PageHeader, CellMono, TableStatusBadge } from '../common';
import type { DataTableColumn } from '../common';
import { AsyncList } from '../common/AsyncList';
import { apiBaseUrl } from '../../authConfig';
import { useConnections, type Connection } from './useConnections';

const useStyles = makeStyles({
  section: {
    marginBottom: tokens.spacingVerticalXXL,
  },
  sectionTitle: {
    display: 'block',
    marginBottom: tokens.spacingVerticalM,
  },
  card: {
    padding: tokens.spacingVerticalL,
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(180px, 240px) 1fr',
    rowGap: tokens.spacingVerticalS,
    columnGap: tokens.spacingHorizontalL,
    margin: 0,
  },
  label: {
    color: tokens.colorNeutralForeground3,
  },
  value: {
    margin: 0,
    wordBreak: 'break-all',
  },
});

const columns: DataTableColumn<Connection>[] = [
  { key: 'name', header: 'Connection', render: (c) => c.name },
  { key: 'target', header: 'Target', render: (c) => <CellMono>{c.target}</CellMono> },
  { key: 'status', header: 'Status', render: (c) => <TableStatusBadge status={c.status} /> },
  { key: 'detail', header: 'Detail', render: (c) => c.detail ?? '' },
];

export function SettingsPage() {
  const styles = useStyles();
  const { accounts } = useMsal();
  const connections = useConnections();
  const env = import.meta.env;

  // Values compiled into this build — real configuration, not samples.
  const environment: Array<[string, string]> = [
    ['Build', env.PROD ? 'Production' : `Development (${env.MODE})`],
    ['API base URL', apiBaseUrl],
    ['Operator sign-in tenant', env.VITE_AZURE_TENANT_ID || 'Not configured'],
    ['Operator app registration', env.VITE_AZURE_CLIENT_ID || 'Not configured'],
    ['Signed in as', accounts[0]?.username ?? 'Unknown'],
  ];

  return (
    <div>
      <PageHeader
        title="Settings"
        description="How this Deployment Manager is configured and what it is connected to."
      />

      <section className={styles.section}>
        <Title3 className={styles.sectionTitle}>Environment</Title3>
        <Card className={styles.card}>
          <dl className={styles.grid}>
            {environment.map(([label, value]) => (
              <div key={label} style={{ display: 'contents' }}>
                <dt className={styles.label}>{label}</dt>
                <dd className={styles.value}>{value}</dd>
              </div>
            ))}
          </dl>
        </Card>
      </section>

      <section className={styles.section}>
        <Title3 className={styles.sectionTitle}>Connections</Title3>
        <AsyncList
          state={connections}
          columns={columns}
          keyExtractor={(c) => c.id}
          loadingLabel="Checking connections..."
          empty={{
            icon: <PlugConnectedRegular />,
            title: 'Connection status not available yet',
            description:
              'The status of FirstIQ Guardian, the customer consent app registration and package storage will appear here once the Deployment Manager API reports its configuration.',
          }}
        />
      </section>
    </div>
  );
}
